import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { authService } from '../../services/auth.service';
import { dashboardChoices } from './DashboardSwitcher';
import BrandLogo from '../ui/BrandLogo';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [dashboards, setDashboards] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const syncAuthState = () => {
      const token = localStorage.getItem('token');
      setIsLoggedIn(Boolean(token));
      setDashboards(token ? dashboardChoices() : []);
    };

    syncAuthState();
    window.addEventListener('storage', syncAuthState);
    window.addEventListener('auth-profile-updated', syncAuthState);


  return () => {
      window.removeEventListener('storage', syncAuthState);
      window.removeEventListener('auth-profile-updated', syncAuthState);
    };
  }, [location.pathname]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await authService.logout();
      setIsLoggedIn(false);
      setDashboards([]);
      setIsMobileMenuOpen(false);
      navigate('/login');
    } catch (err) {
      console.error("Logout failed", err);
      // Fallback: clear tokens anyway
      localStorage.clear();
      setIsLoggedIn(false);
      setDashboards([]);
      setIsMobileMenuOpen(false);
      navigate('/login');
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };



    const menuProps = (target, mobile = false) => {
    const partner = new URLSearchParams(location.search).get('role') === 'CAR_OWNER';
    const active = target === '/register?role=CAR_OWNER' ? location.pathname === '/register' && partner
      : target === '/drivers' ? location.pathname === '/drivers' || location.pathname.startsWith('/drivers/') || location.pathname === '/driver-trips'
      : target === '/browse' ? location.pathname === '/browse' || location.pathname.startsWith('/car/')
      : location.pathname === target;
    return {
      'aria-current': active ? 'page' : undefined,
      className: `${mobile ? 'rounded-md px-3 py-2 ' : 'py-2 border-b-2 ' }font-medium transition-colors ${active ? (mobile ? 'bg-brand-orange-50 text-brand-orange-500' : 'border-brand-orange-500 text-brand-orange-500') : (mobile ? 'hover:bg-gray-50 hover:text-black' : 'border-transparent hover:text-black')}`,
    };
  };

  return (
    <nav className="bg-white shadow-sm font-medium z-50 relative">
      <div className="flex items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2">
          <BrandLogo size="md" />
        </Link>

        <div className="hidden md:flex items-center gap-5 text-sm text-gray-600">
          <Link to="/" {...menuProps('/')}>Home</Link>
          <Link to="/browse" {...menuProps('/browse')}>Browse Cars</Link>
          <Link to="/drivers" {...menuProps('/drivers')}>Book a Driver</Link>
          <Link to="/driver" {...menuProps('/driver')}>Driver Portal</Link>

          {isLoggedIn && dashboards.length > 0 && <details key={location.pathname} className="relative"><summary className="cursor-pointer py-2 font-medium">Dashboards</summary><div className="absolute right-0 top-full z-50 w-52 rounded-xl bg-white border border-gray-100 shadow-lg p-2">{dashboards.map(item => <Link key={item.role} to={item.path} {...menuProps(item.path, true)} className={`block rounded-lg px-3 py-3 text-sm ${location.pathname===item.path?'bg-orange-50 text-orange-700':'hover:bg-stone-50'}`}>{item.label}</Link>)}<div className="border-t border-stone-100 mt-1 pt-1"><Link to="/account/notifications" className="block px-3 py-3 text-sm hover:bg-stone-50">Notifications</Link><Link to="/account/settings" className="block px-3 py-3 text-sm hover:bg-stone-50">Settings</Link></div></div></details>}

          {!isLoggedIn && (
            <Link to="/register?role=CAR_OWNER" {...menuProps('/register?role=CAR_OWNER')}>Become Our Partner</Link>
          )}
          <Link to="/about" {...menuProps('/about')}>About Us</Link>
        </div>

        <div className="flex items-center gap-3 md:gap-6">
          <Link to="/" className="hidden md:block text-sm text-gray-600 hover:text-black">Help</Link>
          <div className="hidden md:flex items-center gap-3">
            {!isLoggedIn ? (
              <>
                <Link to="/register" className="text-gray-900 bg-white border border-gray-200 px-5 py-2 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors shadow-sm">
                  Register
                </Link>
                <Link to="/login" className="bg-brand-black text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors shadow-sm">
                  Log In
                </Link>
              </>
            ) : (
              <button
                onClick={handleLogout}
                className="bg-brand-black text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors shadow-sm"
              >
                Log Out
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((current) => !current)}
            className="inline-flex items-center justify-center rounded-md border border-gray-200 p-2 text-brand-black shadow-sm transition-colors hover:bg-gray-50 md:hidden"
            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="border-t border-gray-100 bg-white px-4 py-4 shadow-sm md:hidden">
          <div className="flex flex-col gap-3 text-sm text-gray-700">
            <Link to="/" onClick={closeMobileMenu} {...menuProps('/', true)}>
              Home
            </Link>
            <Link to="/browse" onClick={closeMobileMenu} {...menuProps('/browse', true)}>
              Browse Cars
            </Link>
            <Link to="/drivers" onClick={closeMobileMenu} {...menuProps('/drivers', true)}>Book a Driver</Link>
          <Link to="/driver" onClick={closeMobileMenu} {...menuProps('/driver', true)}>Driver Portal</Link>


            {isLoggedIn && <><Link to="/account/notifications" {...menuProps('/account/notifications', true)}>Notifications</Link><Link to="/account/settings" {...menuProps('/account/settings', true)}>Settings</Link></>}
            {isLoggedIn && dashboards.map(item => <Link key={item.role} to={item.path} onClick={closeMobileMenu} {...menuProps(item.path, true)}>{item.label}</Link>)}

            {!isLoggedIn && (
              <>
                <Link to="/register?role=CAR_OWNER" onClick={closeMobileMenu} {...menuProps('/register?role=CAR_OWNER', true)}>
                  Become Our Partner
                </Link>
          <Link to="/about" onClick={closeMobileMenu} {...menuProps('/about', true)}>
              About Us
            </Link>
                <Link to="/register" onClick={closeMobileMenu} className="rounded-md border border-gray-200 px-3 py-2 text-center font-medium text-gray-900 transition-colors hover:bg-gray-50">
                  Register
                </Link>
                <Link to="/login" onClick={closeMobileMenu} className="rounded-md bg-brand-black px-3 py-2 text-center font-medium text-white transition-colors hover:bg-gray-800">
                  Log In
                </Link>
              </>
            )}

            {isLoggedIn && (
          <Link to="/about" onClick={closeMobileMenu} {...menuProps('/about', true)}>
              About Us
            </Link>
            )}

            {isLoggedIn ? (
            <button
              onClick={handleLogout}
              className="rounded-md bg-brand-black px-3 py-2 text-left font-medium text-white transition-colors hover:bg-gray-800"
            >
              Log Out
            </button>
            ) : (
              <Link to="/" onClick={closeMobileMenu} className="rounded-md px-3 py-2 font-medium transition-colors hover:bg-gray-50 hover:text-black">
                Help
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
